import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@/lib/query';
import { toast } from 'sonner';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useSession } from '@/hooks/useSession';
import { useSetup, useSetupAction } from '@/hooks/useSetup';
import { HowItWorksCard } from './HowItWorksCard';
import { SetupChip, SetupDoneChip } from './SetupChip';
import { SetupRail } from './SetupRail';
import { SetupSheet } from './SetupSheet';
import { onSetupStepComplete } from './stepEvents';
import type { StepConfig } from './steps';
import { useCurrentStep, useIsDesktop, useSetupSteps } from './useSetupSteps';

export const SETUP_COLLAPSED_KEY = (teamId: string) => `billable.setupCollapsed:${teamId}`;
const HOW_SEEN_KEY = (teamId: string) => `billable.setupHowSeen:${teamId}`;

/** The last step gets the "You're set up." moment instead of its own toast. */
function isLastOpen(steps: StepConfig[], id: string, done: (id: string) => boolean): boolean {
  return steps.every((s) => s.id === id || done(s.id));
}

/**
 * The one component the shell mounts. Renders the rail (or the phone sheet),
 * the chip when the rail is collapsed, the "how it works" card on step 1, and a
 * no overlay on the page itself. Nothing while loading, hidden, finished, or
 * without a team; a brief "You're set up." on the response that finishes it.
 *
 * Placement: the rail is a flow element 300px wide, so mount this as a flex
 * sibling to the right of the main area. Everything else is fixed.
 */
export function SetupGuide() {
  const session = useSession();
  const teamId = session.team?.id;
  if (!teamId) return null;
  // Keyed so the per-team localStorage reads start fresh after a team switch.
  return <Guide key={teamId} teamId={teamId} />;
}

function Guide({ teamId }: { teamId: string }) {
  const setup = useSetup();
  const state = setup.data;
  const action = useSetupAction();
  const steps = useSetupSteps();
  const { current, pick } = useCurrentStep(steps, state);
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const qc = useQueryClient();
  // A step completes from data saved on its page (company name, a client, an
  // entry). Re-read the state on every page change so the rail never waits
  // out its stale window after the person moves on.
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: qk.setup });
  }, [location.pathname, qc]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useLocalStorage(SETUP_COLLAPSED_KEY(teamId), false);
  const [howSeen, setHowSeen] = useLocalStorage(HOW_SEEN_KEY(teamId), false);

  // `?setup=1` forces the rail open, then drops the param so a reload does not repeat it.
  const forceOpen = searchParams.get('setup') === '1';
  const showAction = action.mutate;
  useEffect(() => {
    if (!forceOpen || !state) return;
    setCollapsed(false);
    if (state.hidden) showAction('show');
    const next = new URLSearchParams(searchParams);
    next.delete('setup');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen, !!state]);

  // "Show setup guide" brings the rail back open, never a collapsed or dismissed chip.
  const wasHidden = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!state) return;
    if (wasHidden.current && !state.hidden) setCollapsed(false);
    wasHidden.current = state.hidden;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.hidden]);

  // First time the guide loads for this team on this device, open on the "how" card.
  const openedOnHow = useRef(false);
  useEffect(() => {
    if (!state || state.hidden || howSeen || openedOnHow.current) return;
    openedOnHow.current = true;
    pick('how');
  }, [state, howSeen, pick]);

  // Hand the person to the next open step after finishing one on its own page.
  // Guarded so a save that both flips the state and announces itself moves once.
  const advancedFrom = useRef<string | null>(null);
  const advanceFrom = (stepId: string, done: (id: string) => boolean) => {
    if (advancedFrom.current === stepId || current?.id !== stepId) return;
    const i = steps.findIndex((s) => s.id === stepId);
    const step = steps[i];
    if (!step || location.pathname !== step.route) return;
    advancedFrom.current = stepId;
    const next = steps.slice(i + 1).find((s) => !done(s.id));
    if (!next) return;
    window.setTimeout(() => {
      pick(next.id);
      navigate(next.route);
    }, 700);
  };
  const advanceRef = useRef(advanceFrom);
  advanceRef.current = advanceFrom;
  const toastedFor = useRef<string | null>(null);

  // The finishing response is the only one with `justFinished`; hold the
  // moment locally so the next refetch does not cut it short. Not when the
  // guide was hidden: finishing then is silent.
  const [celebrating, setCelebrating] = useState(false);
  const justFinished = !!state?.justFinished && !state.hidden;
  useEffect(() => {
    if (justFinished) setCelebrating(true);
  }, [justFinished]);
  useEffect(() => {
    if (!celebrating) return;
    const timer = window.setTimeout(() => setCelebrating(false), 5000);
    return () => window.clearTimeout(timer);
  }, [celebrating]);

  useEffect(
    () =>
      onSetupStepComplete((id) => {
        const i = steps.findIndex((s) => s.id === id);
        if (i < 0 || !state) return;
        const doneNow = (sid: string) => steps.find((s) => s.id === sid)?.isDone(state) ?? false;
        if (!steps[i].isDone(state) && toastedFor.current !== id && !isLastOpen(steps, id, doneNow)) {
          toastedFor.current = id;
          toast.success(`Step ${i + 1} done`, { description: steps[i].title });
        }
        advanceRef.current(id, (sid) => sid === id || (steps.find((s) => s.id === sid)?.isDone(state) ?? false));
      }),
    [steps, state]
  );

  // A step that flips to done while mounted gets a toast. The first state is the baseline.
  const prevDone = useRef<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!state) return;
    const now: Record<string, boolean> = {};
    for (const step of steps) now[step.id] = step.isDone(state);
    const prev = prevDone.current;
    if (prev) {
      steps.forEach((step, i) => {
        if (prev[step.id] === false && now[step.id]) {
          if (toastedFor.current !== step.id && !isLastOpen(steps, step.id, (id) => now[id] ?? false)) {
            toastedFor.current = step.id;
            toast.success(`Step ${i + 1} done`, { description: step.title });
          }
          advanceFrom(step.id, (id) => now[id] ?? false);
        }
      });
    }
    prevDone.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, steps]);

  if (celebrating) return <SetupDoneChip onClose={() => setCelebrating(false)} />;
  if (setup.isPending || !state || state.hidden || state.finished) return null;

  const total = steps.length;
  const showHow = !collapsed && current?.id === 'how' && location.pathname === '/timesheet';
  const showChip = collapsed && !state.chipDismissed;

  function finishHow() {
    setHowSeen(true);
    const next = steps[1];
    if (!next) return;
    pick(next.id);
    navigate(next.route);
  }

  // Skipping the tour or a step marks it done and moves to the next open one.
  function skipTour() {
    setHowSeen(true);
    action.mutate({ action: 'skipStep', step: 'how' });
    const next = steps.slice(1).find((s) => (state ? !s.isDone(state) : true));
    if (next) {
      pick(next.id);
      navigate(next.route);
    }
  }

  function go() {
    setCollapsed(false);
    if (current) navigate(current.route);
  }

  return (
    <>
      {isDesktop ? (
        collapsed ? null : (
          <SetupRail
            state={state}
            steps={steps}
            current={current}
            onPick={pick}
            onCollapse={() => setCollapsed(true)}
          />
        )
      ) : collapsed ? null : (
        <SetupSheet state={state} steps={steps} current={current} onPick={pick} />
      )}

      {showChip ? <SetupChip state={state} steps={steps} next={current} onGo={go} /> : null}

      {showHow ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[rgba(24,24,27,0.42)] p-4">
          <HowItWorksCard
            total={total}
            nextLabel={steps[1] ? `Next: ${steps[1].title.toLowerCase()}` : 'Done'}
            onNext={finishHow}
            onSkip={skipTour}
          />
        </div>
      ) : null}

{/* No coach mark: the page renders normally and the rail carries the prompt. */}
    </>
  );
}
